import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card } from '../ui/card';
import {
  Plus, Building2, MapPin, Bed, Users, Trash2, Eye, DoorOpen,
  Search, TrendingUp, IndianRupee, Home, LayoutGrid, List as ListIcon
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import axios from 'axios';
import { emit, EVT } from '../../services/dataSyncBus';
import { track, trackFirstTime } from '../../services/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TYPE_BADGE = {
  'Co-Living':  'bg-purple-100 text-purple-800',
  'Hostel/PG':  'bg-blue-100 text-blue-800',
  'Flat':       'bg-teal-100 text-teal-800',
  'Studio':     'bg-amber-100 text-amber-800',
};

// Inline SVG sparkline — no deps
const Sparkline = ({ values = [], width = 96, height = 28, color = '#10b981', testId }) => {
  if (!values || values.length === 0 || values.every(v => !v)) {
    return <span className="text-[10px] text-gray-400 italic" data-testid={testId}>no data</span>;
  }
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;
  const stepX = values.length > 1 ? width / (values.length - 1) : width;

  const points = values.map((v, i) => {
    const x = i * stepX;
    const y = height - ((v - min) / range) * (height - 4) - 2;
    return [x, y];
  });
  const path = points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const areaPath = `${path} L${width},${height} L0,${height} Z`;

  const first = values[0];
  const last  = values[values.length - 1];
  const trend = last - first;
  const trendColor = trend > 0 ? '#10b981' : trend < 0 ? '#ef4444' : '#6b7280';
  const total = values.reduce((a, b) => a + b, 0);

  return (
    <div className="flex items-center gap-1.5" data-testid={testId}>
      <svg width={width} height={height} className="block">
        <path d={areaPath} fill={trendColor} fillOpacity="0.12" />
        <path d={path} fill="none" stroke={trendColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        {points.length > 0 && (
          <circle cx={points[points.length - 1][0]} cy={points[points.length - 1][1]} r="2.5" fill={trendColor} />
        )}
      </svg>
      <div className="flex flex-col leading-tight">
        <span className="text-[10px] text-gray-500 whitespace-nowrap">₹{total >= 100000 ? `${(total / 100000).toFixed(1)}L` : total >= 1000 ? `${(total / 1000).toFixed(1)}k` : total.toFixed(0)}</span>
        <span className="text-[10px] font-medium whitespace-nowrap" style={{ color: trendColor }}>
          {trend > 0 ? '↑' : trend < 0 ? '↓' : '→'} {Math.abs(trend) >= 1000 ? `${(Math.abs(trend) / 1000).toFixed(1)}k` : Math.abs(trend).toFixed(0)}
        </span>
      </div>
    </div>
  );
};

const PropertiesList = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [propertyToDelete, setPropertyToDelete] = useState(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [view, setView] = useState('table'); // table | grid
  const [trends, setTrends] = useState({}); // { property_id: [6 month revenue values] }
  const [formData, setFormData] = useState({
    name: '', property_type: 'Co-Living', address: '', city: '', state: '',
    pincode: '', total_rooms: 0, monthly_rent_range: '₹5000-₹15000', amenities: [],
  });

  const fetchProperties = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/properties`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProperties(response.data);
    } catch (error) {
      console.error('Error fetching properties:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchTrends = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/properties/trends?months=6`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrends(res.data?.trends || {});
    } catch (e) {
      // Non-blocking — sparkline just won't render
      console.warn('Trends fetch failed:', e?.message);
    }
  }, [token]);

  useEffect(() => { fetchProperties(); fetchTrends(); }, [fetchProperties, fetchTrends]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/properties`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      emit(EVT.PROPERTY, { action: 'created' });
      track('property_created', { property_type: formData.property_type, total_rooms: formData.total_rooms });
      trackFirstTime('first_property_created', { property_type: formData.property_type });
      toast({ title: "Property Added!", description: "Property has been added successfully" });
      setShowAddForm(false);
      setFormData({ name: '', property_type: 'Co-Living', address: '', city: '', state: '', pincode: '', total_rooms: 0, monthly_rent_range: '₹5000-₹15000', amenities: [] });
      fetchProperties();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to add property", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (property) => {
    setPropertyToDelete(property.id);
    setShowDeleteConfirm(true);
  };

  const handleDeleteProperty = async () => {
    if (!propertyToDelete) return;
    try {
      await axios.delete(`${API}/properties/${propertyToDelete}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: "Property Deleted", description: "Property has been deleted successfully" });
      setShowDeleteConfirm(false);
      setPropertyToDelete(null);
      fetchProperties();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to delete property", variant: "destructive" });
    }
  };

  const filtered = useMemo(() => {
    return properties.filter(p => {
      const s = search.toLowerCase();
      const matchSearch = !s ||
        p.name?.toLowerCase().includes(s) ||
        p.city?.toLowerCase().includes(s) ||
        p.address?.toLowerCase().includes(s);
      const matchType = typeFilter === 'all' || p.property_type === typeFilter;
      return matchSearch && matchType;
    });
  }, [properties, search, typeFilter]);

  // Aggregate stats
  const stats = useMemo(() => {
    const totalBeds = properties.reduce((a, p) => a + (p.total_beds || 0), 0);
    const occBeds   = properties.reduce((a, p) => a + (p.occupied_beds || 0), 0);
    const totalRooms = properties.reduce((a, p) => a + (p.total_rooms || 0), 0);
    const occPct    = totalBeds > 0 ? Math.round((occBeds / totalBeds) * 100) : 0;
    return {
      totalProps: properties.length,
      totalRooms,
      totalBeds,
      occBeds,
      occPct,
    };
  }, [properties]);

  if (loading && properties.length === 0) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading properties...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="properties-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Properties</h1>
          <p className="text-gray-600 mt-1">Manage your properties, rooms, occupancy and revenue</p>
        </div>
        <Button
          data-testid="add-property-btn"
          onClick={() => setShowAddForm(!showAddForm)}
          className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
        >
          <Plus size={18} /> Add Property
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="p-4 border-l-4 border-l-blue-500" data-testid="stat-total-properties">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">Total Properties</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{stats.totalProps}</p>
            </div>
            <Building2 className="text-blue-400" size={26} />
          </div>
        </Card>
        <Card className="p-4 border-l-4 border-l-indigo-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">Total Rooms</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{stats.totalRooms}</p>
            </div>
            <DoorOpen className="text-indigo-400" size={26} />
          </div>
        </Card>
        <Card className="p-4 border-l-4 border-l-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">Total Beds</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{stats.totalBeds}</p>
            </div>
            <Bed className="text-purple-400" size={26} />
          </div>
        </Card>
        <Card className="p-4 border-l-4 border-l-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">Occupied Beds</p>
              <p className="text-2xl font-bold text-green-700 mt-1">{stats.occBeds}</p>
            </div>
            <Users className="text-green-400" size={26} />
          </div>
        </Card>
        <Card className="p-4 border-l-4 border-l-orange-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">Occupancy</p>
              <p className="text-2xl font-bold text-orange-600 mt-1">{stats.occPct}%</p>
            </div>
            <TrendingUp className="text-orange-400" size={26} />
          </div>
        </Card>
      </div>

      {/* Add Property Form */}
      {showAddForm && (
        <Card className="p-6 border-2 border-blue-200">
          <h3 className="text-xl font-bold mb-4">Add New Property</h3>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Property Name</label>
              <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="e.g., Sunshine PG" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Property Type</label>
              <select
                value={formData.property_type}
                onChange={(e) => setFormData({...formData, property_type: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option>Co-Living</option>
                <option>Hostel/PG</option>
                <option>Flat</option>
                <option>Studio</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
              <Input value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} placeholder="Complete address" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
              <Input value={formData.city} onChange={(e) => setFormData({...formData, city: e.target.value})} placeholder="Bangalore" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
              <Input value={formData.state} onChange={(e) => setFormData({...formData, state: e.target.value})} placeholder="Karnataka" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Pincode</label>
              <Input value={formData.pincode} onChange={(e) => setFormData({...formData, pincode: e.target.value})} placeholder="560001" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Rent Range</label>
              <Input value={formData.monthly_rent_range} onChange={(e) => setFormData({...formData, monthly_rent_range: e.target.value})} placeholder="₹5000-₹15000" />
            </div>
            <div className="md:col-span-2 flex gap-4">
              <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700" disabled={loading}>
                {loading ? 'Adding...' : 'Add Property'}
              </Button>
              <Button type="button" onClick={() => setShowAddForm(false)} variant="outline" className="flex-1">Cancel</Button>
            </div>
          </form>
        </Card>
      )}

      {/* Filters & View Toggle */}
      <Card className="p-4">
        <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              data-testid="properties-search"
              placeholder="Search by name, city, address..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <select
            data-testid="properties-type-filter"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="all">All Types ({properties.length})</option>
            <option value="Co-Living">Co-Living</option>
            <option value="Hostel/PG">Hostel/PG</option>
            <option value="Flat">Flat</option>
            <option value="Studio">Studio</option>
          </select>
          <div className="flex items-center gap-1 border border-gray-200 rounded-lg p-1 bg-gray-50">
            <button
              data-testid="view-table-btn"
              onClick={() => setView('table')}
              className={`px-3 py-1.5 rounded text-sm flex items-center gap-1 ${view === 'table' ? 'bg-white shadow text-blue-600' : 'text-gray-600'}`}
            ><ListIcon size={14} /> Table</button>
            <button
              data-testid="view-grid-btn"
              onClick={() => setView('grid')}
              className={`px-3 py-1.5 rounded text-sm flex items-center gap-1 ${view === 'grid' ? 'bg-white shadow text-blue-600' : 'text-gray-600'}`}
            ><LayoutGrid size={14} /> Grid</button>
          </div>
        </div>
      </Card>

      {/* Empty State */}
      {filtered.length === 0 && !loading ? (
        <Card className="p-12 text-center" data-testid="properties-empty">
          <Building2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">No Properties Found</h3>
          <p className="text-gray-600 mb-4">{properties.length === 0 ? 'Start by adding your first property' : 'Try adjusting your filters'}</p>
          <Button onClick={() => setShowAddForm(true)} className="bg-blue-600 hover:bg-blue-700">
            <Plus size={16} className="mr-2" /> Add Property
          </Button>
        </Card>
      ) : view === 'table' ? (
        /* DENSE TABLE VIEW */
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="properties-table">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Property</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Location</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Rooms</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Beds</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-[140px]">Occupancy</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Rent Range</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">6-Mo Revenue</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((property) => {
                  const totalBeds = property.total_beds || 0;
                  const occBeds = property.occupied_beds || 0;
                  const vacBeds = property.vacant_beds ?? Math.max(0, totalBeds - occBeds);
                  const occPct = totalBeds > 0 ? Math.round((occBeds / totalBeds) * 100) : 0;
                  const typeBadge = TYPE_BADGE[property.property_type] || 'bg-gray-100 text-gray-700';

                  return (
                    <tr key={property.id} className="hover:bg-blue-50/40 transition-colors" data-testid={`property-row-${property.id}`}>
                      {/* Property Name + Type */}
                      <td className="px-4 py-3 align-top">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center shadow-sm flex-shrink-0">
                            <Building2 size={18} />
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold text-gray-900 truncate">{property.name}</p>
                            <span className={`inline-block mt-1 text-[10px] px-2 py-0.5 rounded-full font-medium ${typeBadge}`}>
                              {property.property_type}
                            </span>
                          </div>
                        </div>
                      </td>

                      {/* Location */}
                      <td className="px-4 py-3 align-top">
                        <div className="flex items-start gap-1 text-gray-700 text-xs">
                          <MapPin size={12} className="mt-0.5 flex-shrink-0 text-gray-400" />
                          <div className="min-w-0">
                            <p className="truncate max-w-[220px]">{property.address}</p>
                            <p className="text-gray-500">{property.city}, {property.state} <span className="text-gray-400">· {property.pincode}</span></p>
                          </div>
                        </div>
                      </td>

                      {/* Rooms */}
                      <td className="px-4 py-3 align-top">
                        <div className="flex items-center gap-1 text-gray-900 font-medium">
                          <DoorOpen size={14} className="text-indigo-500" /> {property.total_rooms || 0}
                        </div>
                      </td>

                      {/* Beds */}
                      <td className="px-4 py-3 align-top">
                        <div className="text-gray-900">
                          <span className="font-medium">{totalBeds}</span>
                          <span className="text-gray-400 text-xs"> total</span>
                        </div>
                        <div className="flex items-center gap-2 text-[11px] mt-0.5">
                          <span className="text-green-700 font-medium">{occBeds} occ</span>
                          <span className="text-orange-600 font-medium">{vacBeds} vac</span>
                        </div>
                      </td>

                      {/* Occupancy Bar */}
                      <td className="px-4 py-3 align-top min-w-[140px]">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${occPct >= 80 ? 'bg-green-500' : occPct >= 40 ? 'bg-yellow-500' : 'bg-red-400'}`}
                              style={{ width: `${occPct}%` }}
                            ></div>
                          </div>
                          <span className="text-xs font-semibold text-gray-700 w-9 text-right">{occPct}%</span>
                        </div>
                      </td>

                      {/* Rent Range */}
                      <td className="px-4 py-3 align-top">
                        <div className="flex items-center gap-1 text-green-700 font-semibold text-sm">
                          <IndianRupee size={12} /> {(property.monthly_rent_range || '').replace(/₹/g, '')}
                        </div>
                      </td>

                      {/* 6-Month Revenue Sparkline */}
                      <td className="px-4 py-3 align-top">
                        <Sparkline
                          values={trends[property.id] || []}
                          testId={`property-sparkline-${property.id}`}
                        />
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3 align-top">
                        <span className={`text-[11px] px-2 py-1 rounded-full font-medium ${
                          property.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'
                        }`}>
                          {property.status || 'active'}
                        </span>
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-3 align-top text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            data-testid={`property-view-${property.id}`}
                            onClick={() => navigate(`/manager/properties/${property.id}`)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                            title="View Details"
                          ><Eye size={16} /></button>
                          <button
                            data-testid={`property-delete-${property.id}`}
                            onClick={() => handleDeleteClick(property)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded"
                            title="Delete"
                          ><Trash2 size={16} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      ) : (
        /* GRID VIEW */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="properties-grid">
          {filtered.map((property) => {
            const totalBeds = property.total_beds || 0;
            const occBeds = property.occupied_beds || 0;
            const occPct = totalBeds > 0 ? Math.round((occBeds / totalBeds) * 100) : 0;
            return (
              <Card key={property.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-4 text-white">
                  <div className="flex items-start justify-between mb-2">
                    <Building2 size={24} />
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${property.status === 'active' ? 'bg-green-500' : 'bg-gray-500'}`}>
                      {property.status || 'active'}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold truncate">{property.name}</h3>
                  <p className="text-xs opacity-90">{property.property_type}</p>
                </div>
                <div className="p-4 space-y-3">
                  <div className="flex items-start gap-1 text-gray-600 text-xs">
                    <MapPin size={12} className="mt-0.5 flex-shrink-0" />
                    <span>{property.city}, {property.state}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center border-t border-b py-3">
                    <div>
                      <p className="text-[10px] text-gray-500 uppercase">Rooms</p>
                      <p className="text-lg font-bold text-gray-900">{property.total_rooms || 0}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-500 uppercase">Beds</p>
                      <p className="text-lg font-bold text-gray-900">{totalBeds}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-500 uppercase">Occ%</p>
                      <p className="text-lg font-bold text-green-700">{occPct}%</p>
                    </div>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className={`h-full ${occPct >= 80 ? 'bg-green-500' : occPct >= 40 ? 'bg-yellow-500' : 'bg-red-400'}`} style={{ width: `${occPct}%` }}></div>
                  </div>
                  <div className="pt-2 border-t">
                    <p className="text-[10px] text-gray-500 uppercase mb-1">6-Mo Revenue</p>
                    <Sparkline values={trends[property.id] || []} width={180} height={32} testId={`property-sparkline-grid-${property.id}`} />
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className="flex-1" onClick={() => navigate(`/manager/properties/${property.id}`)}>
                      <Eye size={14} className="mr-1" /> View
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleDeleteClick(property)}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <DeleteConfirmationModal
        open={showDeleteConfirm}
        onClose={() => { setShowDeleteConfirm(false); setPropertyToDelete(null); }}
        onConfirm={handleDeleteProperty}
        title="Delete Property"
        message="Are you sure you want to delete this property? This will also delete all rooms and tenants associated with it."
      />
    </div>
  );
};

export default PropertiesList;
